import { useTranslation } from 'next-i18next'

import useModuleNavigation from '~/bootcamp/hooks/useModuleNavigation'
import { Link } from '~/components/Link'
import { IconName } from '~/components/Icons'
import ArrowDownThinIcon from '~/components/Icons/svgs/ArrowDownThinIcon'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import useIsBootcampGradePage from '~/bootcamp/hooks/useIsBootcampGradePage'
import { GRADE_QUERY_PARAM } from '~/bootcamp/constants/bootcamp.const'
import Button from '~/components/Buttons/Button'
import useBackToCurriculumPath from '~/bootcamp/hooks/useBackToCurriculumPath'

interface Props {
  className?: string
  iconColor?: string
  icon: IconName
  title: string
  path: string
  isShallowRoute: boolean
  borderClass?: string
  onClick?: () => void
}

const getIconComponent = (name: string): ((props: { [key: string]: any }) => React.ReactElement) => {
  switch (name) {
    case 'arrow-down-thin':
      return ArrowDownThinIcon

    default:
      return ArrowDownThinIcon
  }
}

const NavigationButton = ({
  className = 'bg-purple',
  iconColor,
  icon,
  title,
  path,
  isShallowRoute = false,
  borderClass = '',
  onClick,
}: Props): React.ReactElement => {
  return (
    <Link
      className={`w-full h-full flex justify-center items-center rounded ${borderClass} ${className} active:border-8 cursor-pointer`}
      onClick={onClick}
      replace
      scroll={false}
      shallow={isShallowRoute}
      to={path}
    >
      <span className="leading-5 text-xs font-bold">{title}</span>
      <SVGIcon icon={getIconComponent(icon)} pathClassName={iconColor} />
    </Link>
  )
}

const PreviousLevel = ({ className }: { className?: string }): React.ReactElement => {
  const { showPrevious, previousStepPath, isPreviousShallowRoute } = useModuleNavigation()
  const { t } = useTranslation('bootcamp')

  if (!showPrevious) return <div className={className} />

  return (
    <NavigationButton
      className={className}
      icon="arrow-left-bold"
      isShallowRoute={isPreviousShallowRoute}
      path={previousStepPath}
      title={t('previous')}
    />
  )
}

const NextLevel = ({
  className,
  iconColor,
  wrapperClass = '',
  borderClass = 'border-purple',
  shouldShallowRoute = true,
  onClick,
}: {
  className?: string
  iconColor?: string
  wrapperClass?: string
  borderClass?: string
  shouldShallowRoute?: boolean
  onClick?: () => void
}): React.ReactElement => {
  const { showNext, nextStepPath } = useModuleNavigation()
  const isGradePage = useIsBootcampGradePage()

  const { t } = useTranslation('bootcamp')

  if (!showNext || !nextStepPath.length) return <div className="w-20" />

  return (
    <div className={`w-full md:w-80 h-11 max-w-full ${wrapperClass}`}>
      <NavigationButton
        borderClass={borderClass}
        className={className}
        icon="arrow-down-thin"
        iconColor={`${iconColor}`}
        isShallowRoute={shouldShallowRoute}
        onClick={onClick}
        path={nextStepPath + (isGradePage ? `?${GRADE_QUERY_PARAM}=true` : '')}
        title={t('continue')}
      />
    </div>
  )
}

const NextLevelText = ({ className, label }: { className?: string; label?: string }): React.ReactElement => {
  const { showNext, nextStepPath: path } = useModuleNavigation()
  const { t } = useTranslation('bootcamp')

  if (!showNext) return <div className={`w-20 ${className}`} />

  const btnLabel = label || t('next')

  return (
    <span className={`px-6 py-4 rounded  ${className}`}>
      <Link className="flex flex-col items-center" replace scroll={false} shallow to={path}>
        <span className="leading-5 text-xs">{btnLabel}</span>
      </Link>
    </span>
  )
}

const BackToCurriculumPage = ({ className = '' }: { className?: string }): React.ReactElement => {
  const { t } = useTranslation('bootcamp')
  const curriculumPath = useBackToCurriculumPath()
  return (
    <Button
      className={`w-full px-6 py-4 flex justify-center items-center border-2 rounded cursor-pointer ${className}`}
      href={curriculumPath}
    >
      {t('backToMasteryTrack')}
    </Button>
  )
}

export default NavigationButton
export { PreviousLevel, NextLevel, NextLevelText, BackToCurriculumPage }
